import { Injectable } from '@angular/core';
import { ProductCategory, ProductCategoryWithSubcategory, ProductSubCategory } from '../core/interfaces/product.interface';
import { from, Observable, of, shareReplay, switchMap, tap, map } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { ProductCategoryEnum } from '../core/enums/product-category.enum';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class ProductCategoryService {
  url = environment.cafler_backoffice_back_url;

  private categoriesResponseCache?: ProductCategoryWithSubcategory[];

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly translate: TranslateService
  ) { }

  getProductCategoriesParent(): Observable<ProductCategoryWithSubcategory[]> {
    if (this.categoriesResponseCache) {
      return of(this.categoriesResponseCache);
    }

    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.get<ProductCategoryWithSubcategory[]>(`${this.url}/product-categories`, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      }),
      tap(categories => {
        this.categoriesResponseCache = categories ?? [];
      }),
      shareReplay(1)
    );
  }

  getProductCategories(): Observable<ProductCategory[]> {
    return this.getProductCategoriesParent().pipe(
      map(categories =>
        categories.map(({ productCategoryId, productCategoryCode }) => ({ productCategoryId: productCategoryId, productCategoryCode: this.translate.instant('category_code.' + productCategoryCode) }))
      )
    );
  }

  getProductSubCategories(): Observable<ProductSubCategory[]> {
    return this.getProductCategoriesParent().pipe(
      map(categories =>
        categories.flatMap(category => category.subcategories ?? [])
      )
    );
  }

  getProductCategoriesMap(): Observable<Map<number, ProductCategoryWithSubcategory>> {
    return this.getProductCategoriesParent().pipe(
      map((categories) => {
        const map = new Map<number, ProductCategoryWithSubcategory>();
        categories.forEach((category) => {
          map.set(category.productCategoryId, category)
        });
        return map;
      })
    );
  }

  getProductSubCategoriesMap(): Observable<Map<string, ProductSubCategory>> {
    return this.getProductCategoriesParent().pipe(
      map((categories) => {
        const map = new Map<string, ProductSubCategory>();
        categories.forEach((category) => {
          if (category.subcategories) {
            category.subcategories.forEach((subcategory) => {
              map.set(subcategory.subCategoryId, subcategory)
            })
          }
        });
        return map;
      })
    );
  }

  getSubCategoriesByCategoryId(categoryId: ProductCategoryEnum): Observable<ProductSubCategory[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.get<ProductSubCategory[]>(`${this.url}/productsubcategory/${categoryId}`, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      })
    );
  }

  getCategoriesByIds(categoryIds: number[]): Observable<ProductCategory[]> {
    const ids = new Set(categoryIds);
    if (ids.size === 0) {
      return of([]);
    }
    return this.getProductCategoriesParent().pipe(
      map((categories) =>
        categories
          .filter((category) => ids.has(category.productCategoryId))
      )
    );
  }

  getCategoriesBySubcategoryIds(subcategoryIds: string[]): Observable<ProductCategory[]> {
    const ids = new Set(subcategoryIds);
    if (ids.size === 0) {
      return of([]);
    }
    return this.getProductCategoriesParent().pipe(
      map((categories) =>
        categories
          .filter((category) =>
            category.subcategories?.some((sub) => ids.has(sub.subCategoryId))
          )
          .map(({ productCategoryId, productCategoryCode }) => ({
            productCategoryId,
            productCategoryCode: this.translate.instant('category_code.' + productCategoryCode)
          }))
      )
    );
  }

  getSubCategoriesByCategoryIds(categoryIds: ProductCategoryEnum[]): Observable<ProductSubCategory[]> {
    const ids = new Set(categoryIds);

    if (ids.size === 0) {
      return of([]);
    }

    return this.getProductCategoriesParent().pipe(
      map((categories) =>
        categories
          .filter((category) => ids.has(category.productCategoryId))
          .flatMap((category) => category.subcategories ?? [])
      )
    );
  }

}
